import {
	Badge,
	Box,
	BoxOwnProps,
	Alert,
	Button,
	Dropdown,
	Icons,
	Inline,
	Ping,
	Spinner,
	Stack,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import {
	formatDate,
	isAfter,
	isSame,
	isToday,
	isYesterday,
	subtractUnit,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import {
	Fragment,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import {
	TWhatsAppMessage,
	type TWhatsAppMessageStatusEntry,
	TWhatsAppThread,
	useThreadMessages,
} from "../store"
import { useAuthUser } from "../../Auth"
import {
	WhatsAppDeliveryIndicator,
	WhatsAppStatusIcon,
} from "./DeliveryIndicator"
import {
	formatWhatsAppPricingCategory,
	formatWhatsAppPricingType,
	formatWhatsAppStatusLabel,
} from "./utils"
import { WhatsAppMessageBubble } from "./MessageBubble"
import { WhatsAppMessageHtml } from "./MessageHtml"
import { WhatsAppQuotedMessage } from "./QuotedMessage"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { JSONPreview } from "../../Integrations/JsonPreview"
import useSWR from "swr"

// Distance (px) from the bottom of the message list within which the operator
// counts as "at the bottom". Single source for both the scroll-to-latest button
// visibility and the auto-follow-to-bottom behaviour on new messages.
const AT_BOTTOM_THRESHOLD_PX = 48

// Distance (px) from the top of the message list at which scrolling up starts
// pulling the next (older) page.
const LOAD_OLDER_THRESHOLD_PX = 120

// "N unread messages" separator placed before the first message the viewer had
// not read when they opened the thread.
function UnreadDivider({
	count,
	innerRef,
}: {
	count: number
	innerRef?: React.Ref<HTMLDivElement>
}) {
	return (
		<Box ref={innerRef} paddingY="1">
			<Inline justifyContent="center">
				<Box bgColor="primary" rounded="full" paddingX="3" paddingY="1">
					<Text fontSize="sm" fontWeight="semibold" color="primary">
						{count} unread {count === 1 ? "message" : "messages"}
					</Text>
				</Box>
			</Inline>
		</Box>
	)
}

// WhatsApp-style day label for a message's local date: "Today", "Yesterday",
// the weekday name within the past week, then "Ddd, DD MMM" (adding the year
// once it differs from the current one).
function whatsAppDayLabel(date: Date): string {
	if (isToday(date)) {
		return "Today"
	}
	if (isYesterday(date)) {
		return "Yesterday"
	}
	if (isAfter(date, subtractUnit(new Date(), 7, "day"), "day")) {
		return formatDate(date, "dddd")
	}
	return formatDate(
		date,
		isSame(date, new Date(), "year") ? "ddd, DD MMM" : "ddd, DD MMM YYYY"
	)
}

// The centred pill body of a day heading, shared by the in-flow separators and
// the floating overlay chip. `timestamp` (the day's first message, UTC) drives
// the on-hover full-date tooltip via `Time`.
function DayChip({ label, timestamp }: { label: string; timestamp: string }) {
	return (
		<Badge primary>
			<Time fontSize="xs" fontWeight="semibold" timestamp={timestamp}>
				{label}
			</Time>
		</Badge>
	)
}

// In-flow date heading before each day's first message. Not sticky (WhatsApp
// floats a single overlay chip instead — see WhatsAppMessageList); `data-wa-day*`
// let the scroll handler read which day (and its date) is at the top.
function DaySeparator({
	label,
	timestamp,
}: {
	label: string
	timestamp: string
}) {
	return (
		<Box data-wa-day={label} data-wa-day-ts={timestamp} paddingY="1">
			<Inline justifyContent="center">
				<DayChip label={label} timestamp={timestamp} />
			</Inline>
		</Box>
	)
}

/**
 * The messages themselves: day separators, bubbles, unread divider. Pure
 * rendering -- no scroll handling, no read receipts, no pagination. Shared by
 * `WhatsAppMessageList` and `WhatsAppThreadTranscript`.
 */
/**
 * The message's timestamp, doubling as the way into its details: delivery
 * history, why it failed, and the payloads either side of the provider.
 *
 * The time is the trigger rather than a button of its own -- every bubble
 * already carries one, and a second affordance on each would clutter the
 * conversation for something read occasionally.
 *
 * Fetched on open rather than shipped with every message: it is a
 * drill-down, not chat content.
 */
function MessageTimeWithDetails({
	threadId,
	message,
}: {
	threadId: number
	message: TWhatsAppMessage
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()
	const { data, error, isValidating } = useSWR(
		isOpen ? `/wa-threads/${threadId}/messages/${message.id}` : null,
		(cacheKey) =>
			xhr
				.get<{ data: TWhatsAppMessage }>(cacheKey)
				.then((resp: { data: { data: TWhatsAppMessage } }) => resp.data.data),
		{ revalidateOnFocus: false }
	)

	// Every error Meta reported across the delivery attempts, raw. The bubble
	// shows the readable summary; this is what to paste into a support thread.
	const errors = (data?.statuses || [])
		.flatMap(
			(statusItem: TWhatsAppMessageStatusEntry) =>
				statusItem.payload?.errors || []
		)
		.filter(Boolean)

	return (
		<>
			<Button inline onClick={open} title="Message details">
				<Time
					fontSize="sm"
					color="muted"
					timestamp={message.created_at}
					timeFormat
				/>
			</Button>
			<Dialog open={isOpen} onClose={close} title="Message details">
				<Dialog.Body>
					{isValidating && !data ? (
						<Spinner alignCenter padding="4" />
					) : error ? (
						<Alert status="error">Could not load the message details.</Alert>
					) : data ? (
						<Stack gap="4">
							{data.error ? (
								<Alert status="error" title="Delivery failed">
									<Stack gap="1">
										<Text>{data.error.details || data.error.title}</Text>
										{data.error.code ? (
											<Text fontSize="sm" color="muted">
												Meta error code {data.error.code}
											</Text>
										) : null}
									</Stack>
								</Alert>
							) : null}
							{data.test_recipient ? (
								<Alert status="warning" title="Test mode">
									Delivered to +{data.test_recipient} instead of +
									{data.to_number}. The customer did not receive this message.
								</Alert>
							) : null}
							<Stack gap="2">
								{/*
								 * Delivery statuses only exist for messages we sent.
								 * An inbound one has a single meaningful moment -- when
								 * it reached us -- so it says that rather than
								 * reporting an absence of updates it will never get.
								 */}
								<Text fontWeight="semibold">
									{data.direction === "inbound" ? "Received" : "Delivery"}
								</Text>
								{data.direction === "inbound" ? (
									<Inline gap="2" justifyContent="between">
										<Text>Received</Text>
										<Text color="muted" fontSize="sm">
											<Time timestamp={data.created_at} dateFormat timeFormat />
										</Text>
									</Inline>
								) : data.statuses?.length ? (
									data.statuses.map(
										(
											statusItem: TWhatsAppMessageStatusEntry,
											index: number
										) => (
											<Inline
												key={`${statusItem.status}:${statusItem.created_at}:${index}`}
												gap="2"
												justifyContent="between"
											>
												<Inline gap="1" alignItems="center">
													<WhatsAppStatusIcon status={statusItem.status} />
													<Text>
														{formatWhatsAppStatusLabel(statusItem.status)}
													</Text>
												</Inline>
												<Text color="muted" fontSize="sm">
													<Time
														timestamp={statusItem.created_at}
														dateFormat
														timeFormat
													/>
												</Text>
											</Inline>
										)
									)
								) : (
									<Text color="muted" fontSize="sm">
										No delivery updates received yet.
									</Text>
								)}
							</Stack>
							{data.direction === "inbound" ? null : (
								<Stack gap="2">
									<Text fontWeight="semibold">Pricing</Text>
									{data.pricing_billable === null ||
									data.pricing_billable === undefined ? (
										<Text color="muted" fontSize="sm">
											Not reported by WhatsApp yet.
										</Text>
									) : (
										<>
											<Inline gap="2" justifyContent="between">
												<Text>Charge</Text>
												{data.pricing_billable ? (
													<Badge warning>Charged</Badge>
												) : (
													<Badge success>Free</Badge>
												)}
											</Inline>
											<Inline gap="2" justifyContent="between">
												<Text>Category</Text>
												<Text color="muted" fontSize="sm">
													{formatWhatsAppPricingCategory(data.pricing_category)}
												</Text>
											</Inline>
											<Inline gap="2" justifyContent="between">
												<Text>Type</Text>
												<Text color="muted" fontSize="sm">
													{formatWhatsAppPricingType(data.pricing_type)}
												</Text>
											</Inline>
											<Inline gap="2" justifyContent="between">
												<Text>Pricing model</Text>
												<Text color="muted" fontSize="sm">
													{data.pricing_model === "PMP"
														? "Per message"
														: data.pricing_model === "CBP"
															? "Per conversation"
															: "N/A"}
												</Text>
											</Inline>
										</>
									)}
								</Stack>
							)}
							{errors.length ? (
								<Stack gap="1">
									<Text fontWeight="semibold">Errors from WhatsApp</Text>
									<JSONPreview
										data={errors}
										bgColor="danger"
										color="danger"
										borderColor="danger"
									/>
								</Stack>
							) : null}
							{data.request_payload ? (
								<Stack gap="1">
									<Text fontWeight="semibold">Sent to WhatsApp</Text>
									<JSONPreview data={data.request_payload} />
								</Stack>
							) : null}
							{data.raw_payload ? (
								<Stack gap="1">
									<Text fontWeight="semibold">Received from WhatsApp</Text>
									<JSONPreview data={data.raw_payload} />
								</Stack>
							) : null}
						</Stack>
					) : null}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

/**
 * "Edited" under a message its writer changed: the customer on their phone,
 * or the business in the WhatsApp Business app. Clicking it shows the texts
 * it had before, oldest first, so what was first sent is never lost.
 */
function MessageEditHistory({
	inbound,
	editedAt,
	history,
}: {
	inbound: boolean
	editedAt: string
	history: NonNullable<TWhatsAppMessage["edit_history"]>
}) {
	const [open, setOpen] = useState(false)

	return (
		<Stack gap="1" paddingX="1">
			<Text
				as="button"
				type="button"
				fontSize="xs"
				color="muted"
				onClick={() => setOpen((value) => !value)}
				title={`${
					inbound ? "Edited by the customer" : "Edited in WhatsApp Business app"
				}. Click to see earlier versions.`}
			>
				Edited · <Time timestamp={editedAt} timeFormat />
			</Text>
			{open
				? history.map((version) => (
						<Box
							key={version.replaced_at}
							paddingX="2"
							paddingY="1"
							rounded="md"
							borderWidth="1"
						>
							<Text fontSize="xs" color="muted">
								Until <Time timestamp={version.replaced_at} timeFormat />
							</Text>
							<Text fontSize="sm" whiteSpace="preserveLine">
								{version.message || "(no text)"}
							</Text>
						</Box>
					))
				: null}
		</Stack>
	)
}

/**
 * Emoji reactions, grouped so three thumbs-up read as one chip with a count
 * rather than three chips.
 */
function MessageReactions({
	reactions,
}: {
	reactions?: TWhatsAppMessage["reactions"]
}) {
	const grouped = useMemo(() => {
		const counts = new Map<string, { count: number; names: string[] }>()
		Object.values(reactions || {}).forEach((reaction) => {
			const entry = counts.get(reaction.emoji) || { count: 0, names: [] }
			entry.count += 1
			if (reaction.name) {
				entry.names.push(reaction.name)
			}
			counts.set(reaction.emoji, entry)
		})
		return Array.from(counts.entries())
	}, [reactions])

	if (!grouped.length) {
		return null
	}

	return (
		<Inline gap="1" paddingX="1">
			{grouped.map(([emoji, { count, names }]) => (
				<Box
					key={emoji}
					paddingX="2"
					rounded="full"
					borderWidth="1"
					bgColor="default"
					title={names.join(", ")}
				>
					<Text fontSize="sm">
						{emoji}
						{count > 1 ? ` ${count}` : ""}
					</Text>
				</Box>
			))}
		</Inline>
	)
}

/**
 * Whether WhatsApp can quote the message in a reply. A media send still on
 * its way has only a placeholder id that Meta has never seen.
 */
function canReplyTo(message: TWhatsAppMessage): boolean {
	return !message.message_id.startsWith("pending:")
}

/**
 * The bubble's menu of things to do with the message, beside its time. It
 * opens toward the middle of the conversation: rightward from a customer's
 * bubble on the left, leftward from ours on the right.
 */
function MessageActions({
	inbound,
	onReply,
}: {
	inbound: boolean
	onReply: () => void
}) {
	return (
		<Dropdown alignRight={!inbound} noCaret>
			<Dropdown.ToggleButton size="sm" level="tertiary" title="Message actions">
				<Icons.ChevronDown size="3" />
			</Dropdown.ToggleButton>
			<Dropdown.Menu>
				<Dropdown.MenuItem
					onClick={(e: React.MouseEvent) => {
						e.preventDefault()
						onReply()
					}}
				>
					<Icons.Reply /> Reply
				</Dropdown.MenuItem>
			</Dropdown.Menu>
		</Dropdown>
	)
}

function WhatsAppMessageBubbles({
	thread,
	messages,
	dividerBeforeId,
	unreadCount = 0,
	dividerRef,
	onReply,
}: {
	thread: TWhatsAppThread
	messages: TWhatsAppMessage[]
	dividerBeforeId?: number | null
	unreadCount?: number
	dividerRef?: React.Ref<HTMLDivElement>
	// Picks a message to reply to. Left out where the conversation is only
	// read, so its bubbles offer no reply.
	onReply?: (message: TWhatsAppMessage) => void
}) {
	// Only two parties talk in a thread: the customer (all inbound) and the WABA
	// (all outbound). So inbound bubbles need no name; outbound bubbles show the
	// sending teammate's name as subtext — omitted when it's the current operator.
	const { user } = useAuthUser()
	const authUserId = user?.id

	// The day heading to render before each message that opens a new calendar day.
	const dayHeadingByMessageId = useMemo(() => {
		const headings = new Map<number, { label: string; timestamp: string }>()
		let previousDayKey: string | null = null
		for (const message of messages) {
			const localDate = utcTimestampToLocalDate(message.created_at)
			const dayKey = formatDate(localDate, "YYYY-MM-DD")
			if (dayKey !== previousDayKey) {
				headings.set(message.id, {
					label: whatsAppDayLabel(localDate),
					timestamp: message.created_at,
				})
				previousDayKey = dayKey
			}
		}
		return headings
	}, [messages])

	return (
		<Stack gap="2">
			{messages.map((message) => {
				// Prefer the server-computed `direction` (BSUID-safe). Fall back
				// to the legacy from_number comparison for older payloads that
				// pre-date the `direction` field.
				const isSystem = message.direction === "system"
				const isInbound = isSystem
					? false
					: message.direction
						? message.direction === "inbound"
						: message.from_number === thread.contact?.contact_number

				const bubble = isSystem ? (
					<Inline justifyContent="center">
						<Box
							paddingX="3"
							paddingY="2"
							rounded="md"
							borderWidth="1"
							bgColor="subtle"
							borderColor="default"
							style={{ maxWidth: "80%" }}
						>
							<Stack gap="1">
								<Inline gap="2" alignItems="center">
									<Badge title="WhatsApp system event">System</Badge>
									<Time
										fontSize="sm"
										color="muted"
										timestamp={message.created_at}
										timeFormat
									/>
								</Inline>
								<Text fontSize="sm" whiteSpace="preserveLine">
									{message.message || "Identity update"}
								</Text>
							</Stack>
						</Box>
					</Inline>
				) : (
					<WhatsAppMessageBubble
						inbound={isInbound}
						muted={Boolean(message.revoked_at)}
						below={
							<>
								<MessageReactions reactions={message.reactions} />
								{!isInbound &&
								message.created_by &&
								message.created_by.id !== authUserId ? (
									<Box paddingX="1">
										<Text fontSize="xs" color="muted">
											{message.created_by.name}
										</Text>
									</Box>
								) : null}
								{message.sent_via ? (
									<Box paddingX="1">
										<Text
											fontSize="xs"
											color="muted"
											title={
												message.sent_via === "business_app"
													? "Sent from the WhatsApp Business app on the phone"
													: "Imported from the WhatsApp Business app's past chats"
											}
										>
											{message.sent_via === "business_app"
												? "Sent from WhatsApp Business app"
												: "Imported from WhatsApp Business app"}
										</Text>
									</Box>
								) : null}
								{message.edited_at ? (
									<MessageEditHistory
										inbound={isInbound}
										editedAt={message.edited_at}
										history={message.edit_history ?? []}
									/>
								) : null}
								{message.revoked_at ? (
									<Box paddingX="1">
										<Text fontSize="xs" color="danger">
											{isInbound
												? "Deleted by the customer"
												: "Deleted in WhatsApp Business app"}{" "}
											· <Time timestamp={message.revoked_at} timeFormat />
										</Text>
									</Box>
								) : null}
							</>
						}
					>
						{message.context_message ? (
							<WhatsAppQuotedMessage quoted={message.context_message} />
						) : null}
						{message.template?.name ? (
							<Text fontSize="sm" color="muted">
								Template: {message.template.name}
							</Text>
						) : null}
						{message.test_recipient ? (
							// The integration was in test mode: the customer
							// never got this, the tester did.
							<Box>
								<Badge
									warning
									title={`Test mode: delivered to +${message.test_recipient}, not to +${message.to_number}`}
								>
									Test mode · sent to +{message.test_recipient}
								</Badge>
							</Box>
						) : null}
						{message.rendered_html ? (
							<WhatsAppMessageHtml html={message.rendered_html} />
						) : (
							<Text whiteSpace="preserveLine">
								{message.message || "Template message"}
							</Text>
						)}
						{message.error ? (
							// A bare "failed" sent agents to the integrations
							// screen to find out why. Meta's own words belong
							// on the message that failed.
							<Inline
								gap="1"
								alignItems="start"
								padding="2"
								rounded="md"
								bgColor="danger"
							>
								<Box flexShrink="0" color="danger">
									<Icons.AttentionSolid size="4" />
								</Box>
								<Text color="danger">
									{message.error.details ||
										message.error.title ||
										"Delivery failed"}
								</Text>
							</Inline>
						) : null}
						<Inline gap="2" alignItems="center" justifyContent="end">
							{onReply && canReplyTo(message) ? (
								<MessageActions
									inbound={isInbound}
									onReply={() => onReply(message)}
								/>
							) : null}
							{!isInbound && message.pricing_billable ? (
								// Free messages are the norm inside the service
								// window; only a charge is worth the space.
								<Badge
									title={`Charged by WhatsApp as ${formatWhatsAppPricingCategory(message.pricing_category)}`}
								>
									<Icons.BankNotes size="3" />{" "}
									{formatWhatsAppPricingCategory(message.pricing_category)}
								</Badge>
							) : null}
							<MessageTimeWithDetails threadId={thread.id} message={message} />
							{!isInbound ? (
								<WhatsAppDeliveryIndicator message={message} />
							) : null}
						</Inline>
					</WhatsAppMessageBubble>
				)

				const dayHeading = dayHeadingByMessageId.get(message.id)

				return (
					<Fragment key={message.id}>
						{dayHeading ? (
							<DaySeparator
								label={dayHeading.label}
								timestamp={dayHeading.timestamp}
							/>
						) : null}
						{dividerBeforeId != null && message.id === dividerBeforeId ? (
							<UnreadDivider count={unreadCount} innerRef={dividerRef} />
						) : null}
						{bubble}
					</Fragment>
				)
			})}
		</Stack>
	)
}

/**
 * The scrollable message history. Auto-scrolls to the newest message and clears
 * the operator's unread pointer whenever unseen inbound messages are on screen.
 * `fillHeight` grows to fill a flex column (inbox); otherwise a fixed max-height
 * (the trip-page card).
 */
export function WhatsAppMessageList({
	thread,
	messages,
	onRefresh,
	onLoadOlder,
	hasMoreOlder = false,
	isLoadingOlder = false,
	openBoundary,
	openUnread,
	fillHeight = false,
	maxHeight = "28rem",
	scrollToBottomSignal,
	onReply,
}: {
	thread: TWhatsAppThread
	// The (paginated) messages to render, oldest→newest. Owned by the caller's
	// `useThreadMessages` so the list never loads the whole history at once.
	messages: TWhatsAppMessage[]
	onRefresh: () => Promise<unknown>
	// Pull the next older page; wired to a scroll-up near the top.
	onLoadOlder?: () => void
	hasMoreOlder?: boolean
	isLoadingOlder?: boolean
	// The read pointer and unread count at the moment the thread was opened,
	// sourced from the (always-fresh) list click. The detail's own SWR payload can
	// be stale on a revisit, so these are preferred when provided; they fall back
	// to the thread payload for direct loads (deep-link / refresh).
	openBoundary?: number | null
	openUnread?: number
	fillHeight?: boolean
	maxHeight?: string
	// Bumped by the composer after the operator sends a message, to force a scroll
	// to the newest message regardless of where they had scrolled to.
	scrollToBottomSignal?: number
	// Hands a message to the composer to reply to.
	onReply?: (message: TWhatsAppMessage) => void
}) {
	const containerRef = useRef<HTMLDivElement>(null)
	const dividerRef = useRef<HTMLDivElement>(null)
	const dividerScrolledForRef = useRef<number | null>(null)
	const bottomInitForRef = useRef<number | null>(null)
	const [isAtBottom, setIsAtBottom] = useState(true)
	// Newest message id the operator has caught up to (last time they were at the
	// bottom). Messages after it, while scrolled up, are the "new since" count.
	const seenMaxIdRef = useRef(0)
	const [newSinceScrollUp, setNewSinceScrollUp] = useState(0)
	// While an older page loads, the distance-from-bottom to restore afterwards so
	// prepended messages don't jump the viewport (distance-from-bottom is invariant
	// under a prepend).
	const olderRestoreRef = useRef<number | null>(null)
	// The floating day chip (WhatsApp-style): shows the day at the top of the
	// viewport while scrolling, then slides back out of sight once scrolling stops.
	const [floatingDay, setFloatingDay] = useState<{
		label: string
		timestamp: string
	} | null>(null)
	const [floatingDayVisible, setFloatingDayVisible] = useState(false)
	const hideFloatingDayTimer = useRef<ReturnType<typeof setTimeout> | null>(
		null
	)

	// Slide the chip back out after a short delay; hovering it cancels the pending
	// hide so the operator can read (or hover for the full date) uninterrupted.
	const scheduleFloatingDayHide = useCallback((delay: number) => {
		if (hideFloatingDayTimer.current) {
			clearTimeout(hideFloatingDayTimer.current)
		}
		hideFloatingDayTimer.current = setTimeout(() => {
			setFloatingDayVisible(false)
		}, delay)
	}, [])

	const onScroll = useCallback(() => {
		const container = containerRef.current
		if (!container) {
			return
		}
		setIsAtBottom(
			container.scrollHeight - container.scrollTop - container.clientHeight <
				AT_BOTTOM_THRESHOLD_PX
		)

		// Near the top → pull the next older page. Record the distance-from-bottom
		// so the restore effect can keep the current messages in view after the
		// prepend.
		if (
			onLoadOlder &&
			hasMoreOlder &&
			!isLoadingOlder &&
			olderRestoreRef.current === null &&
			container.scrollTop < LOAD_OLDER_THRESHOLD_PX
		) {
			olderRestoreRef.current = container.scrollHeight - container.scrollTop
			onLoadOlder()
		}

		// The current day = the last in-flow separator that has reached (or passed)
		// the top edge of the viewport.
		const containerTop = container.getBoundingClientRect().top
		const separators = Array.from(
			container.querySelectorAll<HTMLElement>("[data-wa-day]")
		)
		let current: HTMLElement | null = null
		for (const separator of separators) {
			if (separator.getBoundingClientRect().top - containerTop <= 8) {
				current = separator
			} else {
				break
			}
		}
		if (!current && separators.length) {
			current = separators[0]
		}
		if (current?.dataset.waDay && current.dataset.waDayTs) {
			setFloatingDay({
				label: current.dataset.waDay,
				timestamp: current.dataset.waDayTs,
			})
			setFloatingDayVisible(true)
			scheduleFloatingDayHide(1200)
		}
	}, [scheduleFloatingDayHide, onLoadOlder, hasMoreOlder, isLoadingOlder])

	// Clear the pending hide timer on unmount.
	useEffect(() => {
		return () => {
			if (hideFloatingDayTimer.current) {
				clearTimeout(hideFloatingDayTimer.current)
			}
		}
	}, [])

	const scrollToBottom = useCallback(() => {
		const container = containerRef.current
		if (container) {
			container.scrollTop = container.scrollHeight
		}
		setIsAtBottom(true)
		setNewSinceScrollUp(0)
	}, [])

	// Jump to the newest message whenever the operator sends one, even if they had
	// scrolled up. The signal changes after the send's refresh, so the new message
	// is already in the DOM.
	useEffect(() => {
		if (scrollToBottomSignal) {
			scrollToBottom()
		}
	}, [scrollToBottomSignal, scrollToBottom])

	// Track how many messages arrived since the operator was last at the bottom,
	// to badge the scroll-to-latest button (distinct from the unread divider).
	useEffect(() => {
		const maxId = messages.length ? messages[messages.length - 1].id : 0
		if (isAtBottom) {
			seenMaxIdRef.current = maxId
			setNewSinceScrollUp(0)
		} else {
			setNewSinceScrollUp(
				messages.filter(
					(m) => m.id > seenMaxIdRef.current && m.direction === "inbound"
				).length
			)
		}
	}, [messages, isAtBottom])

	// After an older page prepends, restore the viewport to the same messages.
	useLayoutEffect(() => {
		const container = containerRef.current
		if (container && olderRestoreRef.current !== null) {
			container.scrollTop = container.scrollHeight - olderRestoreRef.current
			olderRestoreRef.current = null
		}
	}, [messages])

	// Freeze the read boundary + whether there were unread messages, once per
	// thread open. Because these come from the list click (or a fresh direct
	// load), there's no dependency on the detail's cache freshness. `hadUnread`
	// gates the divider so a thread opened as read never grows one from later
	// live messages; the frozen boundary survives the auto mark-read refetch.
	const [snapshot, setSnapshot] = useState<{
		id: number
		boundary: number
		hadUnread: boolean
	} | null>(null)
	useEffect(() => {
		setSnapshot({
			id: thread.id,
			boundary: openBoundary ?? thread.last_read_message_id ?? 0,
			hadUnread: (openUnread ?? thread.unread_count ?? 0) > 0,
		})
		// Capture once per thread open; the open-time values are frozen here.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [thread.id])

	const dividerBeforeId = useMemo(() => {
		if (!snapshot || snapshot.id !== thread.id || !snapshot.hadUnread) {
			return null
		}
		return messages.find((m) => m.id > snapshot.boundary)?.id ?? null
	}, [snapshot, thread.id, messages])

	// Live count of unread (inbound) messages past the frozen boundary, so the
	// divider label grows as new messages arrive while the thread is open.
	const dividerCount = useMemo(() => {
		if (!snapshot || snapshot.id !== thread.id || !snapshot.hadUnread) {
			return 0
		}
		return messages.filter(
			(m) =>
				m.id > snapshot.boundary &&
				(m.direction === "inbound" || m.status === "received")
		).length
	}, [snapshot, thread.id, messages])

	// Scroll: centre the unread divider the first time it appears for this thread
	// (priority over the initial bottom scroll, since the fresh payload may land
	// after a stale one). Otherwise pin to the bottom.
	useEffect(() => {
		const container = containerRef.current
		if (!container) {
			return
		}
		if (
			dividerBeforeId != null &&
			dividerRef.current &&
			dividerScrolledForRef.current !== thread.id
		) {
			dividerScrolledForRef.current = thread.id
			bottomInitForRef.current = thread.id
			// Scroll the message list only — never the surrounding page (the trip
			// page is long and scrollable).
			const listRect = container.getBoundingClientRect()
			const dividerRect = dividerRef.current.getBoundingClientRect()
			container.scrollTop +=
				dividerRect.top -
				listRect.top -
				container.clientHeight / 2 +
				dividerRect.height / 2
			return
		}
		// Pin to the bottom once on open (for a thread without an unread divider).
		if (
			dividerScrolledForRef.current !== thread.id &&
			bottomInitForRef.current !== thread.id
		) {
			bottomInitForRef.current = thread.id
			container.scrollTop = container.scrollHeight
			return
		}
		// Afterwards, only follow new messages to the bottom if the operator is
		// already there — never yank them down while they've scrolled up.
		if (isAtBottom) {
			container.scrollTop = container.scrollHeight
		}
	}, [thread.id, messages.length, dividerBeforeId, isAtBottom])

	return (
		<Box
			style={
				fillHeight
					? { flex: 1, minHeight: 0, position: "relative" }
					: { position: "relative" }
			}
		>
			<Box
				ref={containerRef}
				bgColor="inset"
				onScroll={onScroll}
				style={
					fillHeight
						? { height: "100%", overflowY: "auto" }
						: { maxHeight, overflowY: "auto" }
				}
				paddingX="4"
				paddingTop="2"
				paddingBottom="4"
			>
				{isLoadingOlder ? (
					<Box paddingY="2">
						<Spinner alignCenter />
					</Box>
				) : null}
				<WhatsAppMessageBubbles
					thread={thread}
					messages={messages}
					dividerBeforeId={dividerBeforeId}
					unreadCount={dividerCount}
					dividerRef={dividerRef}
					onReply={onReply}
				/>
			</Box>
			{floatingDay ? (
				// A top strip that clips the chip as it slides up out of view, so the
				// clipping is local to the chip — the message container keeps a clean
				// overflow for any future position:sticky content.
				<Box
					paddingTop="3"
					overflow="hidden"
					position="absolute"
					top="0"
					right="0"
					left="0"
					pointerEvents="none"
					zIndex="10"
				>
					<Inline
						justifyContent="center"
						opacity={floatingDayVisible ? "100" : "0"}
						style={{
							transition: "transform 200ms ease, opacity 200ms ease",
							transform: floatingDayVisible
								? "translateY(0)"
								: "translateY(-200%)",
						}}
					>
						<Box
							onMouseEnter={() => {
								if (hideFloatingDayTimer.current) {
									clearTimeout(hideFloatingDayTimer.current)
								}
								setFloatingDayVisible(true)
							}}
							onMouseLeave={() => scheduleFloatingDayHide(800)}
							style={{ pointerEvents: "auto" }}
						>
							<DayChip
								label={floatingDay.label}
								timestamp={floatingDay.timestamp}
							/>
						</Box>
					</Inline>
				</Box>
			) : null}
			{!isAtBottom ? (
				<Box position="absolute" style={{ right: "1rem", bottom: "1rem" }}>
					<Inline gap="1" alignItems="center">
						{newSinceScrollUp > 0 ? (
							<Inline alignItems="center">
								<Ping fontSize="xs" color="danger" />
								<Badge status="success" fullRounded>
									{newSinceScrollUp} new
								</Badge>
							</Inline>
						) : null}
						<Button
							size="sm"
							status="primary"
							onClick={scrollToBottom}
							title="Scroll to latest"
						>
							<Icons.ChevronDown />
						</Button>
					</Inline>
				</Box>
			) : null}
		</Box>
	)
}

/**
 * The conversation, read-only, for use beside a form or anywhere the reader
 * should not act on it. Renders the shared bubbles directly rather than
 * wrapping `WhatsAppMessageList`, so none of the live view's behaviour --
 * marking the thread read, pagination on scroll, the scroll-to-latest
 * affordance -- follows it here. Older pages load on request instead.
 */
export function WhatsAppThreadTranscript({
	thread,
	maxHeight = "60vh",
	bgColor,
	padding,
	rounded,
	borderWidth,
}: {
	thread: TWhatsAppThread
	maxHeight?: string
} & Pick<BoxOwnProps, "bgColor" | "padding" | "borderWidth" | "rounded">) {
	const { messages, loadOlder, hasMoreOlder, isLoading, isLoadingOlder } =
		useThreadMessages(thread.id)
	const containerRef = useRef<HTMLDivElement>(null)
	const landedForRef = useRef<number | null>(null)
	// Distance from the bottom, captured before an older page loads. It is
	// invariant under a prepend, so restoring it keeps the messages the reader
	// was looking at exactly where they were.
	const olderRestoreRef = useRef<number | null>(null)

	// Open at the newest message, once per thread -- never yank the reader back
	// down after they have scrolled up.
	useEffect(() => {
		const container = containerRef.current
		if (!container || !messages.length) {
			return
		}
		if (landedForRef.current === thread.id) {
			return
		}
		landedForRef.current = thread.id
		container.scrollTop = container.scrollHeight
	}, [thread.id, messages.length])

	// Before paint, so the prepended page never flashes at the wrong offset.
	useLayoutEffect(() => {
		const container = containerRef.current
		if (!container || olderRestoreRef.current === null || isLoadingOlder) {
			return
		}
		container.scrollTop = container.scrollHeight - olderRestoreRef.current
		olderRestoreRef.current = null
	}, [messages.length, isLoadingOlder])

	function handleLoadOlder() {
		const container = containerRef.current
		if (container) {
			olderRestoreRef.current = container.scrollHeight - container.scrollTop
		}
		loadOlder()
	}

	if (isLoading && !messages.length) {
		return <Spinner alignCenter padding="4" />
	}

	if (!messages.length) {
		return (
			<Text fontSize="sm" color="muted">
				No messages in this conversation yet.
			</Text>
		)
	}

	return (
		<Box
			ref={containerRef}
			style={{ maxHeight }}
			overflow="auto"
			bgColor={bgColor}
			borderWidth={borderWidth}
			rounded={rounded}
			padding={padding}
		>
			{hasMoreOlder ? (
				<Inline justifyContent="center" paddingY="2">
					<Button size="sm" onClick={handleLoadOlder} disabled={isLoadingOlder}>
						{isLoadingOlder ? "Loading..." : "Load older messages"}
					</Button>
				</Inline>
			) : null}
			<WhatsAppMessageBubbles thread={thread} messages={messages} />
		</Box>
	)
}
